import type { ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { CalendarDays, FolderClock, Library, Radio, Settings2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { AccountMenu } from "./AccountMenu";

const navigation = [
  { to: "/app", label: "Recordings", icon: Library, exact: true },
  { to: "/app/schedule", label: "Schedule", icon: CalendarDays, exact: false },
  { to: "/app/recovery", label: "Recovery", icon: FolderClock, exact: false },
];

const secondaryNavigation = [
  { to: "/app/settings", label: "Settings", icon: Settings2 },
];

export function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const studioMode = /\/app\/sessions\/[^/]+\/studio/.test(location.pathname);
  const recordingRoute = location.pathname === "/app" || /^\/app\/(rooms|sessions)\//.test(location.pathname);
  if (studioMode) return children;

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <aside className="app-sidebar" aria-label="Application navigation">
        <Link className="app-brand" to="/app" aria-label="record a podcast dashboard">
          <span className="brand-mark" aria-hidden="true"><Radio /></span>
          <span className="brand-copy"><strong>record a podcast</strong><small>studio control</small></span>
        </Link>

        <nav className="app-nav" aria-label="Primary">
          <span className="nav-label">Studio</span>
          {navigation.map(({ to, label, icon: Icon, exact }) => (
            <NavLink key={to} to={to} end={exact} className={({ isActive }) => cn("app-nav-item", (isActive || (to === "/app" && recordingRoute)) && "is-active")}>
              <Icon aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <nav className="app-nav app-nav-secondary" aria-label="Account">
          <span className="nav-label">Manage</span>
          {secondaryNavigation.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={({ isActive }) => cn("app-nav-item", isActive && "is-active")}>
              <Icon aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

      </aside>

      <div className="app-account-slot">
        <AccountMenu placement="sidebar" />
      </div>

      <div className="app-main">
        <div className="mobile-app-bar">
          <Link className="app-brand" to="/app"><span className="brand-mark"><Radio /></span><strong>record a podcast</strong></Link>
        </div>
        <div className="app-content" id="main-content" tabIndex={-1}>{children}</div>
        <nav className="mobile-nav" aria-label="Mobile navigation">
          {[...navigation, ...secondaryNavigation.slice(0, 1)].map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === "/app"} className={({ isActive }) => cn((isActive || (to === "/app" && recordingRoute)) && "is-active")}>
              <Icon aria-hidden="true" /><span>{label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
